<script setup lang="ts">
import { useProductLink } from '@/composables/useProductLink';
import { convertUrlToResizer } from '@/utils/utils';

const props = defineProps<{
  productId: string;
  imageUrl: string | null;
  productName: string;
  dateTime: string;
  personCount: number;
  isExpired?: boolean;
}>();

const { productLink } = useProductLink(() => ({ id: props.productId }));
</script>

<template>
  <div class="product-informations">
    <div class="avatar" aria-hidden="true">
      <img
        v-if="imageUrl"
        :src="convertUrlToResizer(imageUrl, 120, 120)"
        :alt="productName"
        style="width:100%;height:100%;object-fit:cover;border-radius:inherit;"
      >
      <template v-else>
        {{ productName?.[0]?.toUpperCase() || 'P' }}
      </template>
    </div>
    <div>
      <a
        v-if="productLink"
        :href="productLink"
        class="product-title product-link"
        :class="{ 'expired-text': isExpired }"
        target="_blank"
        rel="noopener noreferrer"
      >
        {{ productName }}
      </a>
      <p v-else class="product-title" :class="{ 'expired-text': isExpired }">
        {{ productName }}
      </p>
      <p :class="{ 'expired-text': isExpired }">
        {{ dateTime }}
      </p>
      <p :class="{ 'expired-text': isExpired }">
        {{ $t('cart.details.persons', { count: personCount }) }}
      </p>
    </div>
  </div>
</template>

<style scoped lang="css">
.product-informations {
  display: flex;
  align-items: center;
  gap: var(--yb-spacing-base);
  margin: var(--yb-spacing-xs) 0;

  .avatar {
    display: flex;
    flex-shrink: 0;
    justify-content: center;
    align-items: center;
    width: 60px;
    height: 100%;
    background: var(--yb-color-lightgray);
    border-radius: var(--yb-radius-sm);
  }

  > div:last-child {
    min-width: 0;
  }
}

.product-title {
  font-weight: var(--yb-text-semi-bold);
  color: var(--yb-color-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.product-link {
  display: block;
  text-decoration: none;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
}

.expired-text {
  text-decoration: line-through;
}
</style>
